import type { FfuStorey } from '@shared/models/ffu-output.model';
import type {
  ExtractedDrawing,
  ProjectFilePageRaster,
} from '@shared/models/project-file-drawing.model';
import type { StoreyLevel } from '@shared/models/storey-level.model';
import {
  labelledStoreyKind,
  labelLooksLikeGroundFloor,
  storeyRowLabel,
} from '@shared/helpers/extracted-storey-key.helpers';
import {
  formatStoreyLevel,
  isEntranceStoreyLevel,
  storeyLevelPosition,
  storeyLevelPositionIndexes,
} from '@shared/helpers/storey-level.helpers';

/** One plan crop with the file it came from. */
export type PlanSheet = {
  fileId: string;
  fileName: string;
  drawing: ExtractedDrawing;
  /** Page raster the drawing's coordinates refer to. Absent for a legacy image. */
  pageRaster?: ProjectFilePageRaster | null;
};

/**
 * One storey in the building's table, as the output stores it.
 * {@link partName} is the part the storey belongs to, null for the building's own.
 */
export type BuildingStoreyRow = FfuStorey & {
  partName: string | null;
};

/** A part's name and its storeys. */
export type BuildingStoreyPart = {
  name: string;
  storeys: readonly FfuStorey[];
};

type StoreyPreviewBase = {
  key: string;
  storeyLabel: string;
  placement: string;
  partName: string | null;
};

/**
 * One slot per storey: its own plan, the normalplan standing in for it, or a
 * data gap with no drawing to show.
 */
export type StoreyPreview =
  | (StoreyPreviewBase & { kind: 'own'; sheet: PlanSheet })
  | (StoreyPreviewBase & { kind: 'normalplan'; sheet: PlanSheet })
  | (StoreyPreviewBase & { kind: 'data_gap' });

export type StoreyPreviews = {
  /** One slot per våning-table row, in that order. */
  previews: StoreyPreview[];
  /**
   * Plan crops no table row claimed (takplan, unplaced sheets).
   * These are not preview slots.
   */
  unmatchedSheets: PlanSheet[];
};

/** One våning-table row. Members are the same storey and stay one slot. */
export type StoreyPreviewGroup = {
  key: string;
  members: readonly BuildingStoreyRow[];
};

/** The output's storey index as a level, for the shared placement formatting. */
export const storeyIndexLevel = (
  storey: Pick<FfuStorey, 'index'>,
): StoreyLevel | null =>
  storey.index === null ? null : { kind: 'single', index: storey.index };

/** `0 · entré`, `+3`, `−1`, or `—` without an index. */
export const formatStoreyPlacement = (storey: Pick<FfuStorey, 'index'>) =>
  formatStoreyLevel(storeyIndexLevel(storey));

export const isEntranceStorey = (storey: Pick<FfuStorey, 'index'>): boolean =>
  storey.index === 0;

/** Same name as the våning table's Plan cell. */
export const storeyLabel = (
  storey: Pick<FfuStorey, 'name' | 'planNumbers'>,
): string =>
  storeyRowLabel(
    { label: storey.name, planNumbers: storey.planNumbers },
    'Våning',
  );

const sheetIndexes = (sheet: PlanSheet): number[] => {
  const coverage = sheet.drawing.coverage;
  if (coverage?.kind !== 'specific') return [];
  return storeyLevelPositionIndexes(storeyLevelPosition(coverage.level));
};

const coverageLevel = (sheet: PlanSheet): StoreyLevel | null => {
  const coverage = sheet.drawing.coverage;
  if (coverage?.kind !== 'specific') return null;
  return coverage.level;
};

/** Entrance crop: classifier kind, printed entré name, or a plan placed at index 0. */
const isEntranceSheet = (sheet: PlanSheet): boolean => {
  if (sheet.drawing.storeyKind === 'entrance') return true;
  if (labelLooksLikeGroundFloor(sheet.drawing.viewLabel)) return true;
  return isEntranceStoreyLevel(coverageLevel(sheet));
};

/**
 * Normalplan / typical crop. Not only `coverage.kind === 'remaining'` —
 * a typical sheet with no coverage still fills storeys that lack their own plan.
 */
const isWildcardSheet = (sheet: PlanSheet): boolean => {
  if (isEntranceSheet(sheet)) return false;
  if (sheet.drawing.planNumbers.length === 1) return false;
  if (sheet.drawing.coverage?.kind === 'remaining') return true;
  if (sheet.drawing.storeyKind === 'typical') return true;
  if (labelledStoreyKind(sheet.drawing.viewLabel) === 'typical') return true;
  return coverageLevel(sheet)?.kind === 'typical';
};

const sharesPlanNumber = (storey: FfuStorey, sheet: PlanSheet): boolean =>
  storey.planNumbers.some((n) => sheet.drawing.planNumbers.includes(n));

/** The storey's own crop: same plan number, the crop placed at its index, or the entrance crop. */
const sheetMatchesStorey = (storey: FfuStorey, sheet: PlanSheet): boolean => {
  if (isWildcardSheet(sheet)) return false;
  if (sharesPlanNumber(storey, sheet)) return true;
  if (storey.index !== null && sheetIndexes(sheet).includes(storey.index)) {
    return true;
  }
  return isEntranceStorey(storey) && isEntranceSheet(sheet);
};

const ownSheet = (
  storey: FfuStorey,
  sheets: readonly PlanSheet[],
): PlanSheet | undefined =>
  sheets.find(
    (sheet) => !isWildcardSheet(sheet) && sharesPlanNumber(storey, sheet),
  ) ?? sheets.find((sheet) => sheetMatchesStorey(storey, sheet));

const ownSheetForGroup = (
  members: readonly BuildingStoreyRow[],
  sheets: readonly PlanSheet[],
): PlanSheet | undefined => {
  for (const member of members) {
    const found = ownSheet(member, sheets);
    if (found !== undefined) return found;
  }
  return undefined;
};

/** One part name when the row is a single part. Several parts stay unlabeled. */
const groupPartName = (
  members: readonly BuildingStoreyRow[],
): string | null => {
  const first = members[0]?.partName ?? null;
  if (members.every((member) => member.partName === first)) return first;
  return null;
};

/** Bottom to top by index. A storey without an index goes last. */
export const compareStoreyRows = (
  a: Pick<FfuStorey, 'index' | 'planNumbers'>,
  b: Pick<FfuStorey, 'index' | 'planNumbers'>,
): number => {
  if (a.index !== null && b.index !== null && a.index !== b.index) {
    return a.index - b.index;
  }
  if ((a.index === null) !== (b.index === null))
    return a.index === null ? 1 : -1;
  return (
    (a.planNumbers[0] ?? Number.MAX_SAFE_INTEGER) -
    (b.planNumbers[0] ?? Number.MAX_SAFE_INTEGER)
  );
};

/**
 * Index of the only section that has storeys, or null when there are none
 * or several. Plan groups that did not match a section key attach there, so
 * one building keeps one preview. Several storey sections are left unmatched.
 */
export const soleStoreySectionIndex = (
  hasStoreys: readonly boolean[],
): number | null => {
  let found: number | null = null;
  for (const [index, has] of hasStoreys.entries()) {
    if (!has) continue;
    if (found !== null) return null;
    found = index;
  }
  return found;
};

/**
 * The building's storeys and its parts' storeys as the output stores them,
 * bottom to top, the building's own before the parts in their order. Nothing
 * is inferred: index, name and plan numbers are the pipeline's.
 */
export const buildingStoreyRows = (input: {
  storeys: readonly FfuStorey[];
  parts: readonly BuildingStoreyPart[];
}): BuildingStoreyRow[] => {
  const order = new Map<string | null, number>([[null, -1]]);
  input.parts.forEach((part, index) => {
    if (!order.has(part.name)) order.set(part.name, index);
  });
  const rows: BuildingStoreyRow[] = [
    ...input.storeys.map((storey) => ({ ...storey, partName: null })),
    ...input.parts.flatMap((part) =>
      part.storeys.map((storey) => ({ ...storey, partName: part.name })),
    ),
  ];
  return rows.sort(
    (a, b) =>
      compareStoreyRows(a, b) ||
      (order.get(a.partName) ?? Number.MAX_SAFE_INTEGER) -
        (order.get(b.partName) ?? Number.MAX_SAFE_INTEGER),
  );
};

/**
 * One slot per våning-table row, in that order. A row without its own crop
 * shows the normalplan, unless the output marks it a data gap.
 */
export const buildStoreyPreviews = (input: {
  groups: readonly StoreyPreviewGroup[];
  sheets: readonly PlanSheet[];
}): StoreyPreviews => {
  const wildcard = input.sheets.find(isWildcardSheet);
  const previews = input.groups.map((group): StoreyPreview => {
    const head = group.members[0];
    const base = {
      key: group.key,
      storeyLabel: head == null ? 'Våning' : storeyLabel(head),
      placement: head == null ? '—' : formatStoreyPlacement(head),
      partName: groupPartName(group.members),
    };
    const sheet = ownSheetForGroup(group.members, input.sheets);
    if (sheet !== undefined) return { ...base, kind: 'own', sheet };
    const gap = group.members.every((member) => member.drawing === 'data_gap');
    if (!gap && wildcard !== undefined) {
      return { ...base, kind: 'normalplan', sheet: wildcard };
    }
    return { ...base, kind: 'data_gap' };
  });
  const claimed = new Set<PlanSheet>(
    input.sheets.filter(
      (sheet) =>
        isWildcardSheet(sheet) ||
        input.groups.some((group) =>
          group.members.some((member) => sheetMatchesStorey(member, sheet)),
        ),
    ),
  );
  return {
    previews,
    unmatchedSheets: input.sheets.filter((sheet) => !claimed.has(sheet)),
  };
};
